// CLEARANCE ZONES: the empty volume a piece of equipment needs around it, exported
// as translucent shells. Off by default, because a model full of red boxes is
// not what anybody opens a model to see.
//
// WHAT I VERIFIED IN THE LIVE DATA BEFORE WRITING THIS, rather than taking it
// from a summary (export 20260926_051328_614, 200 tiles, 112,281 rows read
// from the tiles the loader itself reads):
//
//   clearance rows                     1,331
//   distinct clearance fragment hashes   124
//   distinct real fragment hashes     13,974
//   SHARED BETWEEN THE TWO                 0   <- the whole toggle rests on this
//
// If one hash were both, hiding clearance would hide real geometry with it.
// It is zero on live data today, and the exporter now keys whole-element
// clearances with their own suffix so it is zero by construction going
// forward - but this module NEVER ASSUMES IT. It decides per fragment.
//
// HOW A ROW SAYS IT IS CLEARANCE, in order of authority:
//
//   1. `c: true` on the row. THE EXPORTER'S OWN ANSWER and the only complete
//      one. The JSON sidecar writes it per row; the binary sidecar carries a
//      header array of indexes into the uid table, which sidecarBinary turns
//      into the same `c`. It is OMITTED from tiles with no clearance - 788 of
//      1,445 on the live export - so its absence in one tile means nothing.
//   2. the fragment key, which the exporter spells "Clearence" because that
//      is the Revit FAMILY NAME being copied through, not a typo anybody is
//      going to correct.
//   3. the `#clearance` suffix on a split zone's uid.
//
// 2 and 3 ARE BELT AND BRACES, NOT THE MECHANISM. They miss whole-element
// clearances whose family is not named "Clearence"; the header array does
// not. I had them the other way round for an hour, after reading the header
// of ONE tile - which happened to be a tile with no clearance in it - and
// concluding the field did not exist. Tom's positive control
// (tile_0_7_7_7_7_7_0_0: rows 281, hashes 124, headerLen 40226, clearance
// present with 18 entries) reproduces exactly.

// A BOX THAT HAS CLEARANCE ZONES IS NOT A CLEARANCE ZONE. Tom found the
// exporter's own heuristic matching the word anywhere in a family or type
// name, which would treat "GENERIC BOX WITH CLEARANCE ZONES BY TYPE" as a
// whole clearance element and hide a real piece of equipment behind this
// toggle. He is tightening it exporter-side; this follows the same
// distinction now, because my 57 suspicious rows were that family and I was
// right to doubt them.
//
// EXCLUDED FIRST, because a false positive here HIDES REAL GEOMETRY while a
// false negative only leaves a shell on screen.
const NOT_CLEARANCE = /with[_ -]*clear[ae]nce|clear[ae]nce[_ ]?zones\b/i;

// INSULATION: TRUST THE EXPORTER, DO NOT SECOND-GUESS IT BY NAME.
//
// 2026-09-27, and this is a reversal worth reading before anyone repeats it.
// All 693 host Duct Insulation elements on BA03 carry the exporter's
// clearance flag, and with this switch off by default they were invisible.
// That looked exactly like a false positive, so an exclusion was added here
// that overruled the flag for anything named insulation. IT SHIPPED TO PROD
// AND IT WAS THE REGRESSION.
//
// The owner's rule, in his words: MATERIAL FIRST, then object and family
// names. On SpaceX the modellers USE INSULATION AS CLEARANCE ZONES, so
// insulation carrying the clearance material genuinely IS clearance and
// belongs behind the switch. The exporter already implements exactly that -
// geometry whose material or subcategory is a clearance one is split into a
// #clearance row, and an element whose only geometry is clearance material is
// flagged whole, WHATEVER ITS CATEGORY. The name rule is its fallback, not
// its primary.
//
// So there is no category-based override here on purpose. `row.c` is the
// exporter's material-first answer and it outranks any guess this file could
// make from a string. If insulation ever appears wrongly hidden again, the
// question is whether the MATERIAL is a clearance material - which is a
// question for the model, not for a regex.
// The element IS one: the standalone zone family (Clearence, Clearance_Zone)
// or the |clr / _clr key the exporter gives a split zone.
const CLEARANCE_KEY = /(^|_)clear[ae]nce(_zone)?(_|$)|\|clr\b|_clr(_|$)/i;
const CLEARANCE_UID = /#clearance$/i;

const cache = new Map();

// Is this fragment a clearance shell? Cached: this is asked once per hash per
// tile and there are 122k hashes on the largest model.
export function isClearanceHash(hash) {
  const h = String(hash || '');
  let v = cache.get(h);
  if (v === undefined) {
    v = !NOT_CLEARANCE.test(h) && CLEARANCE_KEY.test(h);
    if (cache.size > 200000) cache.clear();
    cache.set(h, v);
  }
  return v;
}

// A split zone carries it on the uid instead - <elementUid>#clearance.
export const isClearanceUid = (uid) => CLEARANCE_UID.test(String(uid || ''));

// A ROW is clearance if either says so. Both are checked because the
// exporter uses both conventions and they do not always coincide: of the
// 1,331 clearance rows I read, 241 carried the uid suffix.
export const isClearanceRow = (row) => {
  if (!row) return false;
  if (row.c === true) return true;            // the exporter's own answer
  return isClearanceHash(row.h) || isClearanceUid(row.u);
};

// Visibility is per viewer instance, not global: two viewers on one page
// must not toggle each other. The loader hands its own store in.
export function createClearanceState(initiallyVisible = false) {
  let visible = !!initiallyVisible;
  const listeners = new Set();
  return {
    isVisible: () => visible,
    set(next) {
      const v = !!next;
      if (v === visible) return false;       // no work, no redraw, no event
      visible = v;
      listeners.forEach((fn) => { try { fn(v); } catch (e) { /* a listener must not break the toggle */ } });
      return true;
    },
    onChange(fn) { listeners.add(fn); return () => listeners.delete(fn); },
  };
}

// Should this group be kept out of the scene right now?
//
// A group is clearance or it is not - the fragment decides, and clearance
// owns its fragments. There is deliberately no "partly clearance" case: if
// one ever appeared, this returns false and the group is DRAWN, because
// showing a clearance shell by mistake is a cosmetic surprise while hiding
// real geometry by mistake is data loss.
export function shouldHideGroup(hash, rows, state) {
  if (!state || state.isVisible()) return false;
  if (isClearanceHash(hash)) return true;
  // A group every row of which the EXPORTER marked, whatever its family is
  // called - this is the case the name match cannot see.
  if (!Array.isArray(rows) || !rows.length) return false;
  // a whole group of split zones, none of which names itself in the hash
  return rows.every(isClearanceRow);
}
